import {
  NO_REPEAT,
  DAILY_REPEAT,
  WEEKLY_REPEAT
} from "../linear-channels/config/constants";
import { checkAccess, isEmpty } from "@/lib/filters/funcs";
import {
  MEDIA_TYPE_VIDEO,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_LIVE_EVENT,
  MEDIA_TYPE_LIVE_CHANNEL,
  MEDIA_TYPE_VIRTUAL_LIVE_CHANNEL,
  MEDIA_TYPE_VIRTUAL_LIVE_EVENT,
  ROLE_USER_REGULAR,
  ROLE_USER_SEO,
  ROLE_USER_ADMIN,
  SOCIAL_NETWORKS_TYPE_YOUTUBE,
  SOCIAL_NETWORKS_TYPE_TWITTER,
  SOCIAL_NETWORKS_TYPE_FACEBOOK_GROUP,
  SOCIAL_NETWORKS_TYPE_FACEBOOK_PAGE,
  SOCIAL_NETWORKS_TYPE_FACEBOOK_USER,
  SUPPORTED_AUDIO_FORMATS,
  KNOWLEDGE_BASE_ROOT_URL
} from "@/config/constants";
import { components } from "react-select";
import { DateTime as LuxonDateTime } from "luxon";

export const mediaTypeNames = {
  [MEDIA_TYPE_LIVE_EVENT]: "Live Event",
  [MEDIA_TYPE_VIRTUAL_LIVE_EVENT]: "Virtual Live Event",
  [MEDIA_TYPE_LIVE_CHANNEL]: "Live Channel",
  [MEDIA_TYPE_VIRTUAL_LIVE_CHANNEL]: "Virtual Live Channel"
};

export const recordingStatusNames = {
  doNotRecord: "Don't record",
  scheduled: "Transition live event into recorded live event",
  justCopyOriginal: "Just leave the file into new files folder"
};

export const playbackAndIngestStatusNames = {
  autoProvision: "Auto Provision",
  manually: "Enter URLs manually",
  manuallyEndavo: "Enter URLs manually (by Endavo)"
};

export const repeatIntervalNames = {
  [DAILY_REPEAT]: "Daily",
  [WEEKLY_REPEAT]: "Weekly",
  [NO_REPEAT]: "No Repeat"
};

export const MEDIA_USE_TYPE_REGULAR = "regular";
export const MEDIA_USE_TYPE_TRAILER = "trailer";
export const MEDIA_USE_TYPE_COMMENT = "comment";

export const EVENT_ENABLE_AUTOPROVISION_MANUALLY_ENDAVO = "manuallyEndavo";
export const EVENT_ENABLE_AUTOPROVISION_AUTO = "autoProvision";

export const semanticButtonStyles = {
  background: "none",
  color: "#158dc5",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  outline: "none"
};

const ASPECT_RATIO_43 = "4:3";
const ASPECT_RATIO_169 = "16:9";

export const getDefaultAspectRatio = (media) => {
  const firstRendition = media?.renditions?.[0];
  if (!firstRendition || !firstRendition.width || !firstRendition.height) {
    return null;
  }
  const rounded43 = Math.round((4 / 3) * 100) / 100;
  const rounded169 = Math.round((16 / 9) * 100) / 100;

  const { width, height } = firstRendition;

  const roundedCalcRatio = Math.round((width / height) * 100) / 100;

  if (width > 0 && height > 0) {
    if (roundedCalcRatio === rounded43) {
      return ASPECT_RATIO_43;
    } else if (roundedCalcRatio === rounded169) {
      return ASPECT_RATIO_169;
    }
  }
};

export const getIsMediaDeleteDisabled = (
  { canMoveToRecycleBin, internalSyndicationImport, isFullyEncoded },
  userRoles
) => {
  if (internalSyndicationImport) {
    return true;
  }
  if (!isFullyEncoded) {
    return true;
  }
  if (!checkAccess(ROLE_USER_REGULAR, userRoles)) {
    return true;
  }
  if (!canMoveToRecycleBin) {
    return true;
  }
};

export const getDisabledDeleteMessage = (
  { internalSyndicationImport, isFullyEncoded },
  userRoles
) => {
  if (internalSyndicationImport) {
    return "Media from syndication cannot be deleted.";
  }
  if (!isFullyEncoded) {
    return "Media is not fully encoded.";
  }
  if (!checkAccess(ROLE_USER_REGULAR, userRoles)) {
    return "You do not have permissions to delete this media.";
  }
  return "You do not have permissions to delete this media.";
};

export const getSocialMediaIcon = (sncType) => {
  switch (sncType) {
    case SOCIAL_NETWORKS_TYPE_YOUTUBE:
      return "youtube";
    case SOCIAL_NETWORKS_TYPE_TWITTER:
      return "x-twitter";
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_GROUP:
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_PAGE:
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_USER:
      return "facebook";
    default:
      return "";
  }
};

export const getSocialMediaColor = (sncType) => {
  switch (sncType) {
    case SOCIAL_NETWORKS_TYPE_YOUTUBE:
      return "#FF0000";
    case SOCIAL_NETWORKS_TYPE_TWITTER:
      return "#000000";
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_GROUP:
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_PAGE:
    case SOCIAL_NETWORKS_TYPE_FACEBOOK_USER:
      return "#4267B2";
    default:
      return "#2d4155";
  }
};

export const mediaEditRequiredRoles = [
  ROLE_USER_REGULAR,
  ROLE_USER_ADMIN,
  ROLE_USER_SEO
];

export const getKBLink = (media) =>
  `${KNOWLEDGE_BASE_ROOT_URL}${[MEDIA_TYPE_VIDEO, MEDIA_TYPE_AUDIO].includes(media?.mediaType) ? "media-information-page" : "create-live-events"}`;

export const areVisibleRenditionFields = ({
  eventEnableAutoProvision,
  internalSyndicationImport,
  mediaType
}) => {
  const isManuallyOrManuallyEndavo =
    eventEnableAutoProvision === "manually" ||
    eventEnableAutoProvision === EVENT_ENABLE_AUTOPROVISION_MANUALLY_ENDAVO;
  const isLEorLC = [MEDIA_TYPE_LIVE_EVENT, MEDIA_TYPE_LIVE_CHANNEL].includes(
    mediaType
  );
  return (
    isManuallyOrManuallyEndavo && isLEorLC && isEmpty(internalSyndicationImport)
  );
};

const actions = {
  activate: "activated",
  deactivate: "deactivated",
  addCategory: "added to the selected category",
  removeCategory: "removed from the selected category",
  addChannel: "added to the selected channel",
  removeChannel: "removed from the selected channel",
  addGeoTemplate: "added to the selected geo template",
  removeGeoTemplate: "removed from the assigned geo template",
  addRentalPlan: "added to the selected rental plan",
  removeRentalPlan: "removed from the assigned rental plan",
  addRentalBundle: "added to the selected rental bundle",
  removeRentalBundle: "removed from the selected rental bundle",
  moveToRecycleBin: "moved to the recycle bin",
  restoreFromRecycleBin: "restored from the recycle bin",
  delete: "deleted permanently"
};

export const massActionConfirmMessages = {
  moveToRecycleBin:
    "Are you sure you want to move the selected media items to the Recycle Bin?",
  deactivate: "Are you sure you want to deactivate the selected media items?",
  removeGeoTemplate:
    "Are you sure you want to remove the assigned GEO template from the selected media items?",
  removeRentalPlan:
    "Are you sure you want to remove the assigned Rental Plan from the selected media items?",
  restoreFromRecycleBin:
    "Are you sure you want to restore the selected media items?",
  delete:
    "Are you sure you want to permanently delete the selected media items?"
};

export const rentalBundleConfig = {
  label: "Rental Bundle",
  resource: "rentalBundles",
  name: "rentalBundleId",
  autocompleteInputConfig: {
    customOptionComponent: (props) => {
      return (
        <components.Option
          {...props}
          className={props?.data?.isActive === false ? "text-black-50" : ""}
        >
          <span>{props.data.label}</span>
        </components.Option>
      );
    }
  },
  apiParams: {
    "orderBy[name]": "ASC"
  }
};

const channelConfig = {
  label: "Channel",
  resource: "channels",
  name: "channelId",
  apiParams: {
    channelType: "standard",
    "exists[internalSyndicationImport]": false
  }
};

export const massActionsProps = {
  addCategory: {
    label: "Category",
    resource: "categories",
    name: "categoryId"
  },
  removeCategory: {
    label: "Category",
    resource: "categories",
    name: "categoryId"
  },
  addChannel: channelConfig,
  removeChannel: channelConfig,
  addGeoTemplate: {
    label: "Geo Template",
    resource: "geoRestrictionTemplates",
    name: "geoTemplateId",
    apiParams: {
      "exists[internalSyndicationImport]": false
    }
  },
  addRentalPlan: {
    label: "Rental Plan",
    resource: "rentalPlans",
    name: "rentalPlanId",
    apiParams: {
      type: ["vod", "liveEvent"],
      "orderBy[name]": "ASC"
    }
  },
  addRentalBundle: rentalBundleConfig,
  removeRentalBundle: rentalBundleConfig
};

export const getMassActionSuccessMessage = (
  action,
  count,
  modifiedItemsCount
) => {
  return `${modifiedItemsCount} of ${count} item${count > 1 ? "s" : ""} ${modifiedItemsCount === 1 ? "has" : "have"} been ${actions[action]}.`;
};

export const getMediaTableRecycleBinActions = (openModal) => [
  {
    title: "Restore",
    onClick: (id) => {
      openModal({
        isOpen: true,
        entity: `media/restoreFromRecycleBin`,
        params: { confirmId: id }
      });
    }
  },
  {
    title: "Delete",
    onClick: (id) => {
      openModal({
        isOpen: true,
        entity: "media/permanentDelete",
        params: { confirmId: id }
      });
    }
  }
];

export const getRecycleBinMassActions = (openModal) => [
  {
    title: "Restore from Recycle Bin",
    type: "dropdownItem",
    onClick: () => {
      openModal({
        entity: "massActionMedia",
        isOpen: true,
        params: { action: "restoreFromRecycleBin" }
      });
    }
  },
  {
    title: "Delete",
    type: "dropdownItem",
    onClick: () => {
      openModal({
        entity: "massActionMedia",
        isOpen: true,
        params: { action: "delete" }
      });
    }
  }
];

export const getParamsForLimitedAccess = (mediaTypeQueryParam) => {
  const allowedMediaTypes = ["virtualLiveChannel", "virtualLiveEvent"];
  const selectedFilterIsSafe = allowedMediaTypes.includes(mediaTypeQueryParam);
  return {
    mediaType: selectedFilterIsSafe ? mediaTypeQueryParam : allowedMediaTypes
  };
};

export const allowEmbedOptions = [
  {
    title: "Use Default",
    value: "useDefault"
  },
  {
    title: "Yes",
    value: "yes"
  },
  {
    title: "No",
    value: "no"
  }
];

export const MPAARatingOptions = [
  {
    title: "G",
    value: "g"
  },
  {
    title: "PG",
    value: "pg"
  },
  {
    title: "PG13",
    value: "pg13"
  },
  {
    title: "R",
    value: "r"
  },
  {
    title: "NC17",
    value: "nc17"
  }
];

export const shouldFreezeLiveEventEnd = ({
  mode,
  formValues: { liveStartDateTimeApiValue, liveEndDateTimeApiValue }
}) => {
  if (!liveStartDateTimeApiValue && !liveEndDateTimeApiValue) {
    return false;
  }
  if (mode !== "edit") return false;

  const now = LuxonDateTime.now();
  const eventEnd = LuxonDateTime.fromISO(liveEndDateTimeApiValue);

  const freezeStart = eventEnd;
  const freezeEnd = eventEnd.plus({ minutes: 5 });

  return now >= freezeStart && now <= freezeEnd;
};

export const shouldFreeze = ({
  mode,
  formValues: { liveStartDateTimeApiValue, liveEndDateTimeApiValue }
}) => {
  if (!liveStartDateTimeApiValue && !liveEndDateTimeApiValue) {
    return false;
  }
  if (!liveEndDateTimeApiValue) {
    // VLE without items in the scheduler, so no endDateTime
    liveEndDateTimeApiValue = LuxonDateTime.fromISO(liveStartDateTimeApiValue)
      .plus({ hours: 2 })
      .toISO();
  }

  const now = LuxonDateTime.now();
  const start = LuxonDateTime.fromISO(liveStartDateTimeApiValue);
  const end = LuxonDateTime.fromISO(liveEndDateTimeApiValue);

  const freezeStart = start.minus({ minutes: 5 });
  const freezeEnd = end.plus({ minutes: 5 });

  return now >= freezeStart && now <= freezeEnd && mode === "edit";
};

export const subSectionNote = (mode, formValues, isSingleField = false) => {
  return shouldFreeze({ mode, formValues }) &&
    formValues.hasOwnProperty("isSectionFormView")
    ? `You cannot modify ${isSingleField ? "that setting" : "these settings"} after the event has started and within 5 minutes before and after it.`
    : false;
};

export const isRentalPlanFieldVisible = (
  { mediaType, use },
  mode,
  isActivatingUpload
) => {
  const isVideoOrLiveEvent = [
    MEDIA_TYPE_VIDEO,
    MEDIA_TYPE_LIVE_EVENT,
    MEDIA_TYPE_VIRTUAL_LIVE_EVENT
  ].includes(mediaType);
  const isRegularUse = use === MEDIA_USE_TYPE_REGULAR;
  const isCreateLiveEvent =
    mode === "create" &&
    [MEDIA_TYPE_LIVE_EVENT, MEDIA_TYPE_VIRTUAL_LIVE_EVENT].includes(mediaType);
  return (
    (isVideoOrLiveEvent && isRegularUse) ||
    (isActivatingUpload && isRegularUse) ||
    isCreateLiveEvent
  );
};

export const isLiveMediaType = (mediaType) =>
  [
    MEDIA_TYPE_LIVE_EVENT,
    MEDIA_TYPE_VIRTUAL_LIVE_EVENT,
    MEDIA_TYPE_LIVE_CHANNEL,
    MEDIA_TYPE_VIRTUAL_LIVE_CHANNEL
  ].includes(mediaType);

export const getMediaType = (isAddNewTrailer, isActivatingUpload, fileName) => {
  if (!isAddNewTrailer && !isActivatingUpload) {
    return null;
  }
  if (isAddNewTrailer) {
    return MEDIA_TYPE_VIDEO;
  }
  if (isActivatingUpload) {
    // direct upload or cloud upload
    const extension = fileName.slice(-4); // not very reliable actually not all extensions are 3 letters long but the two needed extensions are
    if (SUPPORTED_AUDIO_FORMATS.includes(extension)) {
      return MEDIA_TYPE_AUDIO;
    } else return MEDIA_TYPE_VIDEO;
  }
};
